import React, { useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { KeyRound, Mail, User as UserIcon, Send, ShieldCheck, Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { requestPasswordReset, requestMagicLink, requestSelfVerifyLoginEmail, requestLoginEmailChange } from '../../lib/api';

export const LoginInfoCard = () => {
  const { user } = useAuth();
  const [busy, setBusy] = useState(null);
  const requestBusy = useRef(false);
  const [adding, setAdding] = useState(false);
  const [candidate, setCandidate] = useState('');
  const [changing, setChanging] = useState(false);
  const [changeEmail, setChangeEmail] = useState('');
  const [changeNotice, setChangeNotice] = useState('');

  if (!user) return null;

  const loginEmail = user.email || null;

  const handleReset = async () => {
    if (requestBusy.current) return;
    // Canonical login identity keys only — never contact_email (delivery-only,
    // may be shared by siblings and would resolve to the wrong/parent account).
    const identifier = user.email || user.username;
    if (!identifier) { toast.error('Kunde inte starta lösenordsåterställning.'); return; }
    requestBusy.current = true;
    setBusy('reset');
    try {
      await requestPasswordReset(identifier);
      toast.success('Om kontot kan ta emot återställningsmail skickas en länk till dess registrerade adress.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunde inte skicka återställningslänk');
    } finally { requestBusy.current = false; setBusy(null); }
  };

  const handleMagic = async () => {
    if (requestBusy.current) return;
    const identifier = user.email || user.username;
    if (!identifier) { toast.info('Ingen inloggningsidentifierare tillgänglig'); return; }
    requestBusy.current = true;
    setBusy('magic');
    try {
      await requestMagicLink(identifier, user.name, window.location.origin);
      toast.success('Om kontot kan ta emot inloggningsmail skickas en länk till dess registrerade adress.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunde inte skicka inloggningslänk');
    } finally { requestBusy.current = false; setBusy(null); }
  };

  const handleAddEmail = async () => {
    if (requestBusy.current) return;
    const val = candidate.trim();
    if (!val || !val.includes('@')) { toast.error('Ange en giltig e-postadress'); return; }
    requestBusy.current = true;
    setBusy('add');
    try {
      const r = await requestSelfVerifyLoginEmail(val);
      if (r.data?.status === 'already_linked') {
        toast.success('Kontot använder redan denna e-post för inloggning');
      } else {
        toast.success(`Verifieringsmail skickat till ${val}. Bekräfta adressen för att kunna logga in med den.`);
      }
      setAdding(false); setCandidate('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunde inte skicka verifieringsmailet. Försök igen.');
    } finally { requestBusy.current = false; setBusy(null); }
  };

  const handleEmailChange = async (event) => {
    event.preventDefault();
    if (requestBusy.current) return;
    requestBusy.current = true;
    setBusy('change');
    setChangeNotice('');
    try {
      await requestLoginEmailChange(changeEmail.trim());
      setChangeNotice('Verifieringsmail har skickats till den gamla och den nya adressen. Bekräfta båda inom 60 minuter. Din gamla adress gäller tills bytet är klart.');
      setChanging(false);
      setChangeEmail('');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Adressbytet kunde inte bekräftas. Din nuvarande adress gäller tills båda adresserna har verifierats.');
    } finally {
      requestBusy.current = false;
      setBusy(null);
    }
  };

  const Row = ({ icon: Icon, label, children, helper }) => (
    <div className="space-y-1">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="text-sm text-foreground">{children}</div>
      {helper && <p className="text-xs text-muted-foreground">{helper}</p>}
    </div>
  );

  return (
    <Card className="bg-card border-border" data-testid="login-info-card">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <KeyRound className="w-5 h-5" /> Inloggning
        </CardTitle>
        <p className="text-sm text-muted-foreground">Här ser du hur du loggar in. Länkar skickas enligt kontots registrerade kontaktuppgifter.</p>
      </CardHeader>
      <CardContent className="space-y-5">
        {user.username && (
          <Row icon={UserIcon} label="Användarnamn" helper="Du kan använda ditt användarnamn när du loggar in.">
            <span className="font-mono" data-testid="login-username">{user.username}</span>
          </Row>
        )}

        <Row icon={Mail} label="E-post för inloggning">
          {loginEmail ? (
            <span className="flex flex-wrap items-center gap-2 break-all" data-testid="login-email">
              {loginEmail}
              {user.email_verified && (
                <Badge variant="secondary" className="text-xs gap-1">
                  <ShieldCheck className="w-3 h-3" /> Verifierad
                </Badge>
              )}
            </span>
          ) : adding ? (
            <div className="flex flex-col sm:flex-row gap-2 mt-1">
              <Input
                disabled={Boolean(busy)} type="email" placeholder="din@epost.se" value={candidate}
                onChange={(e) => setCandidate(e.target.value)} autoCapitalize="none"
                data-testid="add-login-email-input" className="bg-background"
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={handleAddEmail} disabled={Boolean(busy)} data-testid="add-login-email-submit">
                  {busy === 'add' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Skicka'}
                </Button>
                <Button size="sm" variant="ghost" disabled={Boolean(busy)} onClick={() => { setAdding(false); setCandidate(''); }}>Avbryt</Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <span className="text-muted-foreground">Ingen egen e-post tillagd</span>
              <Button size="sm" variant="outline" onClick={() => setAdding(true)} data-testid="add-login-email-btn">
                <Plus className="w-3.5 h-3.5 mr-1.5" /> Lägg till e-post
              </Button>
            </div>
          )}
        </Row>

        {loginEmail && (
          <div className="space-y-3 border-t border-border pt-4">
            <p className="text-xs text-muted-foreground">För att byta adress behöver du tillgång till både din gamla och din nya inkorg. Bytet avslutar dina inloggningar när det är klart.</p>
            {changing ? (
              <form onSubmit={handleEmailChange} className="space-y-3">
                <label htmlFor="new-login-email" className="text-sm font-medium">Ny inloggningsadress</label>
                <Input id="new-login-email" type="email" required autoComplete="email" autoCapitalize="none" value={changeEmail} disabled={Boolean(busy)} onChange={e => setChangeEmail(e.target.value)} />
                <div className="flex flex-wrap gap-2">
                  <Button type="submit" disabled={Boolean(busy) || !changeEmail.trim()}>{busy === 'change' ? 'Skickar…' : 'Skicka verifieringsmail'}</Button>
                  <Button type="button" variant="outline" disabled={Boolean(busy)} onClick={() => { setChanging(false); setChangeEmail(''); }}>Avbryt</Button>
                </div>
              </form>
            ) : <Button variant="outline" size="sm" disabled={Boolean(busy)} onClick={() => setChanging(true)}>Byt inloggningsadress</Button>}
            {changeNotice && <p role="status" className="text-sm text-muted-foreground">{changeNotice}</p>}
          </div>
        )}

        {user.contact_email && (
          <Row icon={Mail} label="Kontaktmail"
               helper="Kontaktmail används för leverans och kan tillhöra en förälder. Det är inte nödvändigtvis din inloggningsadress.">
            <span data-testid="contact-email">{user.contact_email}</span>
          </Row>
        )}

        <div className="flex flex-wrap gap-2 pt-1">
          <Button size="sm" variant="outline" onClick={handleReset} disabled={Boolean(busy)} data-testid="change-password-btn">
            <KeyRound className="w-3.5 h-3.5 mr-1.5" /> Byt lösenord
          </Button>
          <Button size="sm" variant="outline" onClick={handleMagic} disabled={Boolean(busy)} data-testid="send-login-link-btn">
            <Send className="w-3.5 h-3.5 mr-1.5" /> Skicka inloggningslänk
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default LoginInfoCard;
